import { type IPayable } from '../@types/Payable.js';
import type { IService } from '../@types/Service.js';
import { BaseError } from '../error/BaseError.js';
import { type CollectionError } from '../error/CollectionError.js';
import type { ClientErrorCode } from '../error/constants.js';
import { MiddlewarePaymentError } from '../error/MiddlewarePaymentError.js';
import { PaymentError } from '../error/PaymentError.js';
import { logger } from '../utils/index.js';
import { type Middleware, pipeline } from '../utils/pipeline.js';
import { subject } from '../utils/subject.js';
import { createMiddlewareContextApi, type MiddlewareContextApi } from './context/createMiddlewareContextApi.js';
import { createPaymentContextApi, type PaymentContextApi } from './context/createPaymentContextApi.js';
import { createPaymentResponseApi, type PaymentResponseApi } from './context/createPaymentResponseApi.js';

type FirstParameter<TFn> = TFn extends (arg: infer R) => unknown ? R : never;

type PaymentMiddleware<TPayable extends IPayable> = MiddlewareContextApi<TPayable>;
type PreMiddleware<TPayable extends IPayable> = PaymentMiddleware<TPayable>;
type PostMiddleware<TPayable extends IPayable> = PaymentMiddleware<TPayable> & {
  paymentResponse: {
    result: PaymentResponseApi<TPayable>;
    error?: CollectionError | undefined;
    clientCode?: ClientErrorCode | undefined;
  };
};
export type PreMiddlewareFn<TPayable extends IPayable = IPayable> = Middleware<PreMiddleware<TPayable>>;
export type PostMiddlewareFn<TPayable extends IPayable = IPayable> = Middleware<PostMiddleware<TPayable>>;

export type ObservableLifecycleHooks<TPayable extends IPayable> = {
  onStart: ReturnType<typeof subject<void>>;
  onFinally: ReturnType<
    typeof subject<Readonly<[PaymentResponseApi<TPayable>, CollectionError | undefined, ClientErrorCode | undefined]>>
  >;
};

export class Payment<TPayable extends IPayable> {
  private readonly _paymentService: IService<TPayable>;
  private readonly _payableItems: TPayable[] = [];

  private readonly _preMiddlewares = pipeline<PreMiddleware<TPayable>>();
  private readonly _postMiddlewares = pipeline<PostMiddleware<TPayable>>();

  private readonly _observers = {
    onStart: subject(),
    onFinally:
      subject<Readonly<[PaymentResponseApi<TPayable>, CollectionError | undefined, ClientErrorCode | undefined]>>(),
  } as const satisfies ObservableLifecycleHooks<TPayable>;

  private _isPaymentInitializedOnce = false;
  /* [
    createPaymentResponseApi(this._paymentService, paymentContextApi),
    paymentContextApi.getPaymentError(),
  ] */
  private _pendingPayment?: Promise<
    readonly [PaymentResponseApi<TPayable>, CollectionError | undefined, ClientErrorCode | undefined]
  > = undefined;

  /**
   * Creates an instance of the Payment class.
   *
   * @param paymentService - An instance of `PaymentService`, mandatory to perform a payment request
   */
  constructor(paymentService: IService<TPayable>) {
    this._paymentService = paymentService;
  }

  /**
   * Lifecycle hooks (callback methods) to execute in the different states that a payment request has.
   * Adopts the pubsub pattern and the hooks can therefore be specified numerous times for separations of concerns.
   *
   * onStart - executes when the payment process starts, no arguments. Useful for eg. starting a spinner etc.
   *
   * @param observer Observer function to be called when the payment process starts
   * @returns The payment instance for chaining
   */
  onStart(observer: FirstParameter<typeof this._observers.onStart.subscribe>): this {
    this._observers.onStart.subscribe(observer);

    return this;
  }

  /**
   * Lifecycle hooks (callback methods) to execute in the different states that a payment request has.
   * Adopts the pubsub pattern and the hooks can therefore be specified numerous times for separations of concerns.
   *
   * onFinally - always executed last in the payment process, takes two arguments:
   * 1) payment response API
   * 2) error object if there were any error during the payment.
   *
   * @param observer Observer function to be called when the payment process is done
   * @returns The payment instance for chaining
   */
  onFinally(observer: FirstParameter<typeof this._observers.onFinally.subscribe>): this {
    this._observers.onFinally.subscribe(observer);

    return this;
  }

  /**
   * Adds a middleware function to be executed before the payable items will be processed.
   * If one middleware fails no further middleware will be called and the payment request(s) will never occur.
   * Useful to create user interaction for eg. when the payment price exceeds the account balance etc.
   *
   * @param middlewares A curried function wheres the first function takes the payment context api
   * and the second function takes the `next` function to be called to execute
   * the next middleware in the chain.
   * @returns The payment instance for chaining
   */
  addPreMiddleware(...middlewares: PreMiddlewareFn<TPayable>[]): this;
  addPreMiddleware(...middlewares: PreMiddlewareFn[]): this;
  addPreMiddleware(...middlewares: (PreMiddlewareFn<TPayable> | PreMiddlewareFn)[]): this {
    if (this._isPaymentInitializedOnce) {
      throw new Error('Not allowed to add middleware after payment initialization');
    }
    this._preMiddlewares.push(...(middlewares as PreMiddlewareFn<TPayable>[]));

    return this;
  }

  /**
   * Adds a middleware function to be executed after the payment request(s) settles.
   * At least one payable item must have been paid for a post middleware stack to be run.
   * Useful to update the context with eg. things like Gräsroten.
   *
   * @param middlewares A curried function wheres the first function takes the payment context api
   * and the second function takes the `next` function to be called to execute
   * the next middleware in the chain.
   * @returns The payment instance for chaining
   */
  addPostMiddleware(...middlewares: PostMiddlewareFn<TPayable>[]): this;
  addPostMiddleware(...middlewares: PostMiddlewareFn[]): this;
  addPostMiddleware(...middlewares: (PostMiddlewareFn<TPayable> | PostMiddlewareFn)[]): this {
    if (this._isPaymentInitializedOnce) {
      throw new Error('Not allowed to add middleware after payment initialization');
    }
    this._postMiddlewares.push(...(middlewares as PostMiddlewareFn<TPayable>[]));

    return this;
  }

  /**
   * Adds an payable item instance(s) to be paid upon execution of payment flow.
   *
   * @param item One or more items to be paid
   * @returns The payment instance for chaining
   */
  addPayable(...item: TPayable[]): this {
    this._payableItems.push(...item);

    return this;
  }

  /**
   * Public function to start the payment process.
   *
   * @throws {PaymentError} Will throw if some sort of validation isn't satisfied before the payment starts
   * @returns A promise that resolves to an array containing two items
   * 1) an object instance of the payment response API, see `createPaymentResponseApi`
   * 2) the original error instance, is undefined if no error ocurred.
   */
  execute(): Promise<
    readonly [PaymentResponseApi<TPayable>, CollectionError | undefined, ClientErrorCode | undefined]
  > {
    this._isPaymentInitializedOnce = true;

    if (this._pendingPayment) {
      // prevent accidental payment requests
      logger.info('Payment already initialized');

      return this._pendingPayment;
    }

    if (this._payableItems.length === 0) {
      throw new PaymentError({ clientCode: 'NOT_ENOUGH_DATA' });
    }

    this._pendingPayment = this._tryPay();

    // Run async payment
    return this._pendingPayment;
  }

  /**
   * Start the payment process and continuously logs the payment process to Kibana.
   * 1) runs pre middlewares
   * 2) executes payment request through the payment service #initiatePaymentRequest
   * 3) runs post middlewares
   * 4) runs cleanup through the payment service #onPaymentDone
   * 5) runs tracking through the payment service #trackPayments
   * 6) payment request over, settles.
   *
   * @returns A promise that resolves to an array containing two items
   * 1) an object instance of the payment response API, see `createPaymentResponseApi`
   * 2) the original error instance, is undefined if no error ocurred.
   */
  private async _tryPay(): Promise<
    readonly [PaymentResponseApi<TPayable>, CollectionError | undefined, ClientErrorCode | undefined]
  > {
    const [mwContextApi, updateMwContextApi] = createMiddlewareContextApi<TPayable>(
      Object.freeze(this._payableItems),
      this._observers
    );
    let paymentError: BaseError<ClientErrorCode> | undefined;
    let paymentContextApi: PaymentContextApi<TPayable> | undefined;

    try {
      logger.info('Payment start', {
        payableCount: mwContextApi.payment.count(),
        totalCost: mwContextApi.payment.getTotalPrice(),
      });

      // this._isPendingPayment = true;
      this._observers.onStart.publish();
      await this._preMiddlewares.execute(mwContextApi);

      paymentContextApi = createPaymentContextApi(mwContextApi);
      const [paymentResults, paymentResultError] = await this._paymentService.initiatePaymentRequest(
        paymentContextApi.createPaymentsIterable()
      );

      // Update payment API with the results from the payment service to align the local context

      paymentResultError && paymentContextApi.mergeCollectionError(paymentResultError);
      paymentContextApi.addPaidItems(paymentResults);

      const successfulPayableItemsCount = paymentContextApi.getPaidPayableItems().length;
      updateMwContextApi.payment.setPaidCount(successfulPayableItemsCount);
    } catch (error) {
      if (error instanceof BaseError) {
        paymentError = error;
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        paymentError = new PaymentError({ clientCode: 'GENERIC', message: errorMessage });
        logger.warn('Unhandled exception during payment', { error: errorMessage });
      }
    } finally {
      const isError = paymentError && !(paymentError instanceof MiddlewarePaymentError);
      paymentContextApi = paymentContextApi ?? createPaymentContextApi(mwContextApi);

      paymentError && isError && paymentContextApi.addPaymentError(paymentError);

      if (paymentError instanceof BaseError) {
        // Intentional errors
        logger.info('Payment exited', { error: paymentError.toString(), code: paymentError.clientCode });
      }

      const totalPayableItems = this._payableItems.length;
      const successfulPayableItems = paymentContextApi.getPaidPayableItems().length;

      if (successfulPayableItems) {
        logger.info('Payment done', { successfulCount: successfulPayableItems, totalCount: totalPayableItems });
      }

      const getPaidPayableItemsData = paymentContextApi.getPaidPayableItemsData();

      this._paymentService.onPaymentDone(getPaidPayableItemsData);
      // paymentService.onPaymentDone(getPaidPayableItemsData as readonly Readonly<{ payable: TPayable; dto: DataTransferObject; }>[]);

      // run tracking after payment
      setTimeout(() => {
        this._paymentService.trackPayments(getPaidPayableItemsData);
      });

      delete this._pendingPayment;
    }

    const response = [
      createPaymentResponseApi(this._paymentService, paymentContextApi),
      paymentContextApi.getPaymentError(),
      paymentError?.clientCode,
    ] as const;

    // Do not wait for post middlewares to finish
    this._postMiddlewares
      .execute(
        Object.freeze({
          ...mwContextApi,
          paymentResponse: {
            result: response[0],
            error: response[1],
          },
        })
      )
      .catch((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.warn('Post middleware failed', { error: errorMessage });
      });

    this._observers.onFinally.publish(response);

    return response;
  }
}

/**
 * This is the main function of the Payment component which creates an Payment object that can adopt
 * lifecycle hooks for the payment process and add middleware functions to be run before and after
 * the payment request(s).
 *
 * @param paymentService - An instance of `PaymentService`, mandatory to perform a payment request
 * @returns The object to add middlewares, lifecycle hooks and execute the payment request
 */
export function createPayment<TPayable extends IPayable>(paymentService: IService<TPayable>): Payment<TPayable> {
  return new Payment(paymentService);
}
